import { LoaderCircle } from 'lucide-react';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ReleaseNoteDescription from '@/components/manage-release-notes/release-note-description';
import ReleaseNotesListItem from '@/components/manage-release-notes/release-notes-list-item';
import ReleaseNotesListHeader from '@/components/manage-release-notes/release-published-notes-list/release-notes-list-header';
import StatusMessage from '@/components/shared/status-message';
import CustomAccordion from '@/components/ui/custom-accordion';
import PaginationTabs from '@/components/ui/pagination-tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import useGetReleases from '@/hooks/use-get-releases';
import { ERROR_FEEDBACK } from '@/utils/constants';

interface ReleasePublishedNotesListProps {
  onBack: () => void;
}

const ReleasePublishedNotesList: FC<ReleasePublishedNotesListProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const { data, isLoading, isError, refetch } = useGetReleases({ page });



  const releases = data?.releases ?? [];
  const count = data?.count ?? 0;

  const isEmpty = !isLoading && !isError && releases.length === 0;

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <ReleaseNotesListHeader onBack={onBack} />
        <div className="flex-1 flex items-center justify-center">
          <LoaderCircle className="size-12 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col h-full">
        <ReleaseNotesListHeader onBack={onBack} />
        <div className="min-h-[60vh] flex items-center justify-center py-8">
          <StatusMessage
            type="error"
            title={t(ERROR_FEEDBACK.general)}
            description={t(ERROR_FEEDBACK.release_note.api)}
            showReloadButton
            onRetry={refetch}
          />
        </div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="flex flex-col h-full">
        <ReleaseNotesListHeader onBack={onBack} />
        <div className="min-h-[60vh] flex items-center justify-center py-8">
          <StatusMessage
            type="empty"
            title={t(ERROR_FEEDBACK.release_note.empty)}
            description={t(ERROR_FEEDBACK.release_note.description)}
            className="items-center text-center"
          />
        </div>
      </div>
    );
  }

  const currentRelease = releases.find(({ isCurrent }) => isCurrent);

  const accordionItems = releases.map((release) => {
    const { id, description } = release;

    return {
      value: id,
      title: <ReleaseNotesListItem release={release} />,
      content: <ReleaseNoteDescription description={description} />,
    };
  });

  return (
    <div className="flex flex-col h-full">
      <ReleaseNotesListHeader onBack={onBack} />
      <ScrollArea className="h-[calc(100vh-150px)] w-full">
        <CustomAccordion
          items={accordionItems}
          defaultValue={currentRelease?.id}
          className="px-3"
          accordionItemClassName=" border-b-0"
          AccordionTriggerClassName="hover:bg-accent px-2 py-2.5 items-center"
          accordionContentClassName="px-2"
        />
        <div className="pr-5">
          <PaginationTabs
            totalCount={count}
            limit={10}
            currentPage={page}
            onPageChange={setPage}
            className="w-auto mx-0 justify-end"
          />
        </div>
      </ScrollArea>
    </div>
  );
};

export default ReleasePublishedNotesList;
